import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, MapPin, Phone, X, Check, Send } from 'lucide-react';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  enter: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

function ContactPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'contact@core4it.dk',
      href: 'mailto:contact@core4it.dk'
    },
    {
      icon: MapPin,
      label: 'Lokation',
      value: 'Copenhagen, Denmark'
    },
    {
      icon: Phone,
      label: 'Telefon',
      value: '+45 53 13 30 69',
      href: 'tel:+4553133069'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.target);

    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData).toString()
      });
      setIsModalOpen(true);
      e.target.reset();
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Oops! Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="enter"
      exit="exit"
      transition={{ duration: 0.4 }}
    >
      {/* Hero */}
      <section className="section" style={{ paddingTop: 'calc(var(--header-height) + var(--space-16))' }}>
        <div className="container">
          <motion.div
            className="section__header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="section__tag">KONTAKT</span>
            <h1 className="section__title">Lad os tage en snak</h1>
            <p className="section__description">
              Har I et projekt i tankerne, eller har I brug for rådgivning? Vi er klar til at hjælpe jer med at finde den rigtige løsning.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section section--alt">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="contact__title">Kontaktoplysninger</h2>
              <p className="contact__text">
                Vi vil gerne høre fra jer. Tag fat i os, så tager vi en snak om,
                hvordan vi kan hjælpe jeres virksomhed videre.
              </p>

              <div className="contact__info">
                {contactInfo.map((item) => (
                  <div key={item.label} className="contact__item">
                    <div className="contact__icon">
                      <item.icon />
                    </div>
                    <div>
                      <h3 className="contact__label">{item.label}</h3>
                      {item.href ? (
                        <a href={item.href} className="contact__value">
                          {item.value}
                        </a>
                      ) : (
                        <p className="contact__value">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <form
                name="contact"
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
                className="contact-form"
                onSubmit={handleSubmit}
              >
                <input type="hidden" name="form-name" value="contact" />
                <p className="sr-only">
                  <label>
                    Don't fill this out if you're human:
                    <input name="bot-field" />
                  </label>
                </p>

                <div className="contact-form__group">
                  <label htmlFor="name" className="contact-form__label">
                    Navn
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="contact-form__input"
                    placeholder="Dit navn"
                    required
                  />
                </div>

                <div className="contact-form__group">
                  <label htmlFor="email" className="contact-form__label">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="contact-form__input"
                    placeholder="dig@virksomhed.dk"
                    required
                  />
                </div>

                <div className="contact-form__group">
                  <label htmlFor="message" className="contact-form__label">
                    Besked
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    className="contact-form__textarea"
                    placeholder="Hvad kan vi hjælpe jer med?"
                    rows={5}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn--primary contact-form__submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="btn__icon" />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Success Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <SuccessModal onClose={() => setIsModalOpen(false)} />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function SuccessModal({ onClose }) {
  return (
    <motion.div
      className="modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="modal__overlay"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      <motion.div
        className="modal__content"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        <button
          type="button"
          className="modal__close"
          onClick={onClose}
          aria-label="Close modal"
        >
          <X />
        </button>

        <div className="modal__icon">
          <Check />
        </div>

        <h2 className="modal__title">Message Sent!</h2>
        <p className="modal__text">
          Tak for at kontakte Core4it. Vi har modtaget din mail, og vil vende retur hurtigst muligt.
        </p>
      </motion.div>
    </motion.div>
  );
}

export default ContactPage;
